// components/dashboard/LocationFilter.jsx
import { useState, useEffect, useRef } from "react"
import { ChevronDown, X, MapPin, Globe } from "lucide-react"

const FF = "'Euclid Circular A', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
const BRAND = "#18181b"

export const ANYWHERE_VALUE = "anywhere-in-india"

export default function LocationFilter({ options, selected, onChange, onClear, anywhereValue, C, renderTrigger, dropdownAlign = "left", wrapperStyle }) {
  const ANYWHERE = anywhereValue ?? ANYWHERE_VALUE
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const count = selected.length
  const isActive = count > 0

  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [open])

  function handleToggle(val) {
    if (val === ANYWHERE) {
      if (selected.length === 1 && selected[0] === ANYWHERE) {
        onChange([])
      } else {
        onChange([ANYWHERE])
      }
    } else {
      const withoutAnywhere = selected.filter(v => v !== ANYWHERE)
      if (withoutAnywhere.includes(val)) {
        onChange(withoutAnywhere.filter(v => v !== val))
      } else {
        onChange([...withoutAnywhere, val])
      }
    }
  }

  const anywhereOpt = options.find(o => o.value === ANYWHERE)
  const cityOptions  = options.filter(o => o.value !== ANYWHERE)
  const isAnywhereChecked = selected.includes(ANYWHERE)

  // Trigger button label
  const btnLabel = isActive && count === 1
    ? (options.find(o => o.value === selected[0])?.label ?? "Location")
    : "Location"

  return (
    <div ref={ref} style={{ position: "relative", ...(wrapperStyle || {}) }}>

      {/* ── Trigger — custom or default pill ── */}
      {renderTrigger ? renderTrigger({ open, toggle: () => setOpen(o => !o), count, label: btnLabel }) : (
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 30, padding: "0 11px",
          background: isActive ? BRAND : C.filterBtnBg,
          border: isActive ? `1px solid ${BRAND}` : `1px solid ${C.filterBtnBorder}`,
          borderRadius: 9999,
          fontSize: 12, fontWeight: isActive ? 600 : 400,
          color: isActive ? "#fff" : C.filterBtnText,
          fontFamily: FF, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
        }}
        onMouseEnter={e => {
          if (!isActive) { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.color = BRAND }
          else e.currentTarget.style.background = "#1d4535"
        }}
        onMouseLeave={e => {
          if (!isActive) { e.currentTarget.style.borderColor = C.filterBtnBorder; e.currentTarget.style.color = C.filterBtnText }
          else e.currentTarget.style.background = BRAND
        }}
      >
        {btnLabel}
        {isActive && count > 1 && (
          <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 9999, padding: "1px 7px", fontSize: 11, fontWeight: 700, lineHeight: "18px" }}>
            {count}
          </span>
        )}
        <ChevronDown size={11} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", opacity: 0.7 }} />
      </button>
      )}

      {/* ── Dropdown ── */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", [dropdownAlign]: 0,
          width: 272,
          background: C.popupBg,
          borderRadius: 16,
          boxShadow: C.isDark
            ? "0 8px 16px -2px rgba(0,0,0,0.5), 0 24px 48px rgba(0,0,0,0.65)"
            : "0 8px 16px -2px rgba(0,0,0,0.08), 0 24px 48px rgba(0,0,0,0.12)",
          zIndex: 200,
          overflow: "hidden",
          animation: "fadeUp 0.15s ease",
        }}>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px 10px",
            borderBottom: `1px solid ${C.popupDivider}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <MapPin size={11} color={C.textTertiary} />
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
                color: C.textTertiary, fontFamily: FF, textTransform: "uppercase",
              }}>
                Location
              </span>
            </div>
            {count > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onClear() }}
                style={{
                  display: "flex", alignItems: "center", gap: 3,
                  fontSize: 11, fontWeight: 600, color: "#dc2626",
                  background: "none", border: "none", cursor: "pointer", fontFamily: FF,
                  padding: 0,
                }}
              >
                <X size={10} /> Clear
              </button>
            )}
          </div>

          <div style={{ padding: "8px 10px 10px" }}>

            {/* ── Anywhere in India — full-width hero row ── */}
            {anywhereOpt && (
              <button
                onClick={() => handleToggle(ANYWHERE)}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 12px",
                  marginBottom: 8,
                  background: isAnywhereChecked
                    ? (C.isDark ? "#1a2e25" : "#eef4f1")
                    : (C.isDark ? "#1e1e1e" : "#f7f7f7"),
                  border: `1px solid ${isAnywhereChecked
                    ? (C.isDark ? "#18181b44" : "#18181b28")
                    : (C.isDark ? "#2e2e2e" : "#e8e8e8")}`,
                  borderRadius: 10,
                  cursor: "pointer", textAlign: "left", fontFamily: FF,
                  transition: "background 0.12s, border-color 0.12s",
                }}
                onMouseEnter={e => {
                  if (!isAnywhereChecked) {
                    e.currentTarget.style.background = C.isDark ? "#242424" : "#f2f2f2"
                    e.currentTarget.style.borderColor = C.isDark ? "#383838" : "#d8dbd6"
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isAnywhereChecked
                    ? (C.isDark ? "#1a2e25" : "#eef4f1")
                    : (C.isDark ? "#1e1e1e" : "#f7f7f7")
                  e.currentTarget.style.borderColor = isAnywhereChecked
                    ? (C.isDark ? "#18181b44" : "#18181b28")
                    : (C.isDark ? "#2e2e2e" : "#e8e8e8")
                }}
              >
                {/* Globe icon container */}
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: isAnywhereChecked
                    ? (C.isDark ? "#18181b22" : "#18181b14")
                    : (C.isDark ? "#2a2a2a" : "#ececec"),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.12s",
                }}>
                  <Globe
                    size={14}
                    color={isAnywhereChecked ? (C.isDark ? "#18181b" : BRAND) : C.textDim}
                    strokeWidth={1.8}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: 13, fontWeight: isAnywhereChecked ? 600 : 500,
                    color: isAnywhereChecked ? (C.isDark ? "#18181b" : BRAND) : C.text,
                    fontFamily: FF, lineHeight: 1.2,
                  }}>
                    {anywhereOpt.label}
                  </p>
                  <p style={{
                    margin: 0, fontSize: 10.5, fontFamily: FF, lineHeight: 1.3, marginTop: 1,
                    color: isAnywhereChecked ? (C.isDark ? "#18181b88" : "#18181b88") : C.textTertiary,
                  }}>
                    All cities included
                  </p>
                </div>

                {/* Checkmark */}
                <div style={{
                  width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                  background: isAnywhereChecked ? BRAND : "transparent",
                  border: isAnywhereChecked ? "none" : `1.5px solid ${C.borderMid}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.12s",
                }}>
                  {isAnywhereChecked && (
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </button>
            )}

            {/* ── Divider with label ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              marginBottom: 6,
            }}>
              <div style={{ flex: 1, height: 1, background: C.popupDivider }} />
              <span style={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em",
                color: C.textTertiary, fontFamily: FF, textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}>
                Cities
              </span>
              <div style={{ flex: 1, height: 1, background: C.popupDivider }} />
            </div>

            {/* ── 2-column city grid ── */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 4,
            }}>
              {cityOptions.map(opt => {
                const isChecked = selected.includes(opt.value)
                const isDisabled = isAnywhereChecked
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleToggle(opt.value)}
                    style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "7px 10px",
                      background: isChecked
                        ? (C.isDark ? "#1a2e25" : "#eef4f1")
                        : "transparent",
                      border: `1px solid ${isChecked
                        ? (C.isDark ? "#18181b33" : "#18181b22")
                        : "transparent"}`,
                      borderRadius: 8,
                      cursor: isDisabled ? "default" : "pointer",
                      textAlign: "left", fontFamily: FF,
                      transition: "background 0.1s, border-color 0.1s",
                      opacity: isDisabled && !isChecked ? 0.4 : 1,
                    }}
                    onMouseEnter={e => {
                      if (!isChecked && !isDisabled) e.currentTarget.style.background = C.isDark ? "#222" : "#f2f2f2"
                    }}
                    onMouseLeave={e => {
                      if (!isChecked) e.currentTarget.style.background = "transparent"
                    }}
                  >
                    {/* Dot indicator */}
                    <div style={{
                      width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                      background: isChecked ? (C.isDark ? "#18181b" : BRAND) : C.borderMid,
                      transition: "background 0.12s",
                    }} />
                    <span style={{
                      fontSize: 12.5,
                      color: isChecked ? (C.isDark ? "#18181b" : BRAND) : C.textMuted,
                      fontWeight: isChecked ? 600 : 400,
                      fontFamily: FF,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {opt.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}